import { lazy, Suspense, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Portal from "@via/pages/projects/portal/portal.tsx";
import Metadata from "@via/pages/projects/metadata/metadata.tsx";
import { SkeletonLoader } from "@via/components/loading/loading";

const Dashboard = lazy(
  () => import("@via/pages/projects/dashboard/dashboard.tsx")
);
const Pipelines = lazy(
  () => import("@via/pages/projects/pipelines/pipelines.tsx")
);
const Runs = lazy(() => import("@via/pages/projects/runs/runs.tsx"));

const ProjectSettings = lazy(
  () => import("@via/pages/projects/settings/settings.tsx")
);

const PersistentRoutes = ({
  horizontalTabOptions,
}: {
  horizontalTabOptions: {
    name: string;
    href: string;
  }[];
}) => {
  const location = useLocation();
  const { projectId } = useParams();
  const navigate = useNavigate();

  const [loadedTab, setLoadedTab] = useState("");
  const [isPortalMounted, setIsPortalMounted] = useState(false);
  const [isMetadataMounted, setIsMetadataMounted] = useState(false);

  // Extract current tab from pathname
  const currentTab = location.pathname.split("/").pop() as string;

  const isValidTab = (tabName: string) => {
    return horizontalTabOptions.some(tab => tab.href === tabName);
  };

  useEffect(() => {
    if (!isValidTab(currentTab)) {
      // If the current tab is not in the list, redirect to dashboard (/projects/:projectId)
      const path = `/projects/${projectId}/dashboard`;
      navigate(path);
    } else {
      setLoadedTab(currentTab);
    }
    // Mount Portal and Metadata tabs if they are visited
    if (currentTab === "portal") {
      setIsPortalMounted(true);
    }
    if (currentTab === "metadata") {
      setIsMetadataMounted(true);
    }
  }, [location, projectId, horizontalTabOptions]);

  return (
    <Suspense
      fallback={
        <div className="grid grid-cols-1 gap-6 py-6 sm:grid-cols-2 lg:grid-cols-3">
          <SkeletonLoader count={12} />
        </div>
      }>
      {loadedTab === "dashboard" && <Dashboard />}
      {loadedTab === "pipelines" && <Pipelines />}
      {loadedTab === "runs" && <Runs />}
      {loadedTab === "settings" && <ProjectSettings />}

      {/* Persist Portal and Metadata tabs */}
      {isPortalMounted && isValidTab("portal") && (
        <div style={{ display: loadedTab === "portal" ? "block" : "none" }}>
          <Portal />
        </div>
      )}
      {isMetadataMounted && isValidTab("metadata") && (
        <div style={{ display: loadedTab === "metadata" ? "block" : "none" }}>
          <Metadata />
        </div>
      )}
    </Suspense>
  );
};

export default PersistentRoutes;
